import { SourceDocumentUIPart } from "ai";
import { useState, useMemo } from "react";
import { cn } from "@/lib/utils";
import ImagesCarrouselDialog from "../Dialogs/ImagesCarrouselDialog";
import { getAllSources, MessagePart } from "./utils";

type ImageThumbnailsProps = {
  parts: MessagePart[];
};

export const ImageThumbnails = ({ parts }: ImageThumbnailsProps) => {
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [startIndex, setStartIndex] = useState(0);

  const images = useMemo(() => {
    const sources = getAllSources(parts);
    const seenFileIds = new Set<string>();

    return sources
      .filter((source): source is SourceDocumentUIPart => {
        if (source.type !== "source-document") return false;
        const mediaType = source.mediaType || "";
        return mediaType.startsWith("image/");
      })
      .map((source) => {
        const docMeta = source.providerMetadata?.noxus as { file_id?: string };
        const documentId = docMeta?.file_id;
        if (!documentId || seenFileIds.has(documentId)) return null;
        seenFileIds.add(documentId);
        return `/api/backend/file/${documentId}?preview=true`;
      })
      .filter((url): url is string => url !== null);
  }, [parts]);

  if (images.length === 0) return null;

  const displayImages = images.slice(0, 3);
  const remainingCount = images.length - 3;
  const showOverlay = remainingCount > 0;

  const handleImageClick = (index: number) => {
    setStartIndex(index);
    setCarouselOpen(true);
  };

  return (
    <>
      <div className="flex gap-2 mt-3">
        {displayImages.map((image, index) => {
          const isLastVisible = index === 2 && showOverlay;

          return (
            <button
              key={image}
              onClick={() => handleImageClick(index)}
              className="relative size-[60px] rounded-md overflow-hidden border border-border cursor-pointer hover:opacity-80 transition-opacity"
            >
              <img
                src={image}
                alt=""
                className={cn(
                  "size-full object-cover",
                  isLastVisible && "blur-sm"
                )}
              />
              {isLastVisible && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                  <span className="text-white font-semibold text-sm">
                    +{remainingCount}
                  </span>
                </div>
              )}
            </button>
          );
        })}
      </div>
      <ImagesCarrouselDialog
        images={images}
        isOpen={carouselOpen}
        onOpenChange={setCarouselOpen}
        startIndex={startIndex}
      />
    </>
  );
};
